import { useState, useEffect, useRef } from 'react';
import SeoHead from '../../components/SeoHead';

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const galleryRef = useRef<HTMLDivElement>(null);

  const heroImages = [
    'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/5487b9a9689809befa7e6abc9f88047f.jpeg',
    'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/a4212e417f4260ea17001a28209407c8.jpeg',
    'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/ca5553b8ebdd084f0e0a9d801ef342a6.jpeg',
    'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/740ce6c578884beec59aee6d6bf304ae.jpeg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (galleryRef.current) {
      const scrollWidth = galleryRef.current.scrollWidth;
      const clientWidth = galleryRef.current.clientWidth;
      const centerPosition = (scrollWidth - clientWidth) / 2;
      galleryRef.current.scrollLeft = centerPosition;
    }
  }, []);

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    const handleScroll = () => {
      const scrollLeft = gallery.scrollLeft;
      const scrollWidth = gallery.scrollWidth;
      const clientWidth = gallery.clientWidth;
      const itemWidth = 320 + 16; // w-80 (320px) + gap-4 (16px)
      const totalItems = galleryImages.length;
      const singleSetWidth = itemWidth * totalItems;

      // 右端に到達したら左端にジャンプ
      if (scrollLeft >= singleSetWidth * 2) {
        gallery.scrollLeft = scrollLeft - singleSetWidth;
      }
      // 左端に到達したら右端にジャンプ
      else if (scrollLeft <= 0) {
        gallery.scrollLeft = scrollLeft + singleSetWidth;
      }
    };

    gallery.addEventListener('scroll', handleScroll);
    return () => gallery.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const galleryImages = [
    {
      alt: 'Tonkotsu ramen',
      src: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/f49ea1413072dc3359629063fe934efd.jpeg'
    },
    {
      alt: 'Fresh sushi',
      src: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/d663042bc111a573d4e6b24e6f6ed712.jpeg'
    },
    {
      alt: 'Crispy karaage',
      src: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/90f7ce22c56289be298eb191079895c0.jpeg'
    },
    {
      alt: 'Spicy tan tan ramen',
      src: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/d6e4cc31fc50590239c0691066be5c89.jpeg'
    },
    {
      alt: 'Delicious dish',
      src: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/ccd47a573488516a8fbc35777e0e3f78.jpeg'
    },
    {
      alt: 'Gyoza dumplings',
      src: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/ee6d48f2bf6e7256510c68d72a0154bb.jpeg'
    },
    {
      alt: 'Karaage don',
      src: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/36b1d460f596866ba3f1069215240757.jpeg'
    }
  ];

  // 無限スクロール用に画像を3セット複製
  const infiniteGalleryImages = [...galleryImages, ...galleryImages, ...galleryImages];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: 'Okuman Karaage & Ramen Bar',
    image: 'https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/5487b9a9689809befa7e6abc9f88047f.jpeg',
    '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
    telephone: '+61744399508',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '6/220 Melbourne St',
      addressLocality: 'South Brisbane',
      addressRegion: 'QLD',
      postalCode: '4101',
      addressCountry: 'AU',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -27.4789,
      longitude: 153.0186,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '11:30',
        closes: '14:30',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '17:00',
        closes: '21:30',
      },
    ],
    servesCuisine: 'Japanese',
    menu: 'https://okumankaraage-ramen-bar.square.site/',
    acceptsReservations: 'True',
  };

  return (
    <>
      <SeoHead structuredData={structuredData} />
      <div className="min-h-screen bg-stone-50">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 bg-stone-900 shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/2c6f5f26358009c83babaf47230293ba.png"
                  alt="Okuman Karaage & Ramen Bar"
                  className="h-16 w-auto"
                />
              </div>
              <div className="hidden md:flex space-x-8">
                <button onClick={() => scrollToSection('home')} className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-stone-300 hover:text-red-500">
                  Home
                </button>
                <button onClick={() => scrollToSection('about')} className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-stone-300 hover:text-red-500">
                  About
                </button>
                <button onClick={() => scrollToSection('order')} className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-stone-300 hover:text-red-500">
                  Order
                </button>
                <button onClick={() => scrollToSection('gallery')} className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-stone-300 hover:text-red-500">
                  Gallery
                </button>
                <button onClick={() => scrollToSection('visit')} className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-red-500">
                  Visit Us
                </button>
              </div>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-stone-300`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden bg-stone-800 px-4 py-4 space-y-2">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-stone-300 hover:text-red-500">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-stone-300 hover:text-red-500">About</button>
              <button onClick={() => scrollToSection('order')} className="block w-full text-left py-2 text-stone-300 hover:text-red-500">Order</button>
              <button onClick={() => scrollToSection('gallery')} className="block w-full text-left py-2 text-stone-300 hover:text-red-500">Gallery</button>
              <button onClick={() => scrollToSection('visit')} className="block w-full text-left py-2 text-red-500">Visit Us</button>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <section id="home" className="relative min-h-[70vh] flex items-center justify-center pt-20 overflow-hidden">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-1000"
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: currentImageIndex === index ? 1 : 0
              }}
            />
          ))}
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 text-center px-4 max-w-4xl mx-auto py-12">
            <h1 className="text-3xl md:text-5xl font-light text-white mb-4 leading-tight drop-shadow-lg" style={{ fontFamily: "'Shadows Into Light', cursive" }}>
              Okuman<br />
              Karaage & Ramen Bar<br />
              in West End
            </h1>
            <p className="text-sm md:text-base text-white mb-8 max-w-2xl mx-auto drop-shadow-md leading-relaxed">
              Japanese Restaurant<br />
              Crispy karaage and authentic, house-made ramen
            </p>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900 mb-6" style={{ fontFamily: "'Shadows Into Light', cursive" }}>
                  About Okuman
                </h2>
                <div className="space-y-4 md:space-y-6 text-stone-700 text-base md:text-lg leading-relaxed">
                  <p>
                    The name Okuman carries a wish: to bring happiness to "oku-man" — millions of people — through our food.<br />
                    It reflects our belief that every guest deserves warmth, care, and a moment of genuine joy.
                  </p>
                  <p>
                    When you step into Okuman, we want you to feel as if you've stepped into Japan.<br />
                    Experience omotenashi with your whole body — the quiet hospitality, the attention to detail, and the comfort of sincere service.
                  </p>
                  <p>
                    Enjoy the crispness of our karaage, the depth of our ramen, and flavors crafted to express the heart of Japanese comfort food.
                  </p>
                  <p>
                    At Okuman, we hope every dish and every moment helps you smile — just as our name promises.
                  </p>
                </div>
              </div>
              <div className="relative h-96 md:h-full min-h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  alt="Okuman Japanese cuisine spread"
                  className="w-full h-full object-cover object-top grayscale"
                  src="https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/52dccd3094f059912488ee4ec7596014.jpeg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Order Section */}
        <section id="order" className="py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-light text-stone-900 mb-6" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Order Your Favorites</h2>
            <p className="text-xl text-stone-600 mb-12 max-w-2xl mx-auto">
              A Little Japanese Magic in Every Bite
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              <a
                href="https://okumankaraage-ramen-bar.square.site/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-stone-50 p-10 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer block w-full"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className="ri-file-list-3-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-light text-stone-900 mb-4 whitespace-nowrap" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Menu / Online Order</h3>
                <p className="text-stone-600 text-base leading-relaxed">Browse menu<br />Order online</p>
              </a>
              <a
                href="tel:0738440789"
                className="bg-stone-50 p-10 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer block w-full"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className="ri-phone-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-light text-stone-900 mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Call to Order</h3>
                <p className="text-stone-600 text-base leading-relaxed">07 3844 0789<br />Phone orders for pickup</p>
              </a>
              <a
                href="https://www.opentable.com.au/booking/restref/availability?lang=en-AU&amp;correlationId=057f1efe-f881-4a4b-b2f9-c96a53a1f14e&amp;restRef=219623&amp;otSource=Restaurant%20website"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-stone-50 p-10 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer block w-full"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className="ri-store-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-light text-stone-900 mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Booking</h3>
                <p className="text-stone-600 text-base leading-relaxed">Visit us and<br />enjoy our meal</p>
              </a>
              <button
                onClick={() => setShowDeliveryModal(true)}
                className="bg-stone-50 p-10 rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer block w-full"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className="ri-e-bike-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-light text-stone-900 mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Delivery</h3>
                <p className="text-stone-600 text-base leading-relaxed">Order through<br />delivery apps</p>
              </button>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-20 px-4 bg-stone-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light text-stone-900 mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Gallery</h2>
              <p className="text-xl text-stone-600">Yummy Food &amp; Cozy Japanese Vibes</p>
            </div>
            <div className="relative">
              <div ref={galleryRef} className="overflow-x-auto scrollbar-hide">
                <div className="flex gap-4 pb-4">
                  {infiniteGalleryImages.map((image, index) => (
                    <div key={index} className="flex-shrink-0 w-80 h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
                      <img
                        alt={image.alt}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                        src={image.src}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Visit Us Section */}
        <section id="visit" className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light text-stone-900 mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Visit Us</h2>
              <p className="text-xl text-stone-600">We&#39;d love to see you at our South Brisbane location</p>
            </div>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <div className="bg-stone-50 p-8 rounded-xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-red-600"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-stone-900 mb-2" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Address</h3>
                      <p className="text-stone-700 text-lg">
                        6/220 Melbourne St<br />
                        South Brisbane QLD 4101<br />
                        Australia
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-stone-50 p-8 rounded-xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-red-600"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-light text-stone-900 mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Phone</h3>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-stone-600 mb-1">AI Assistant (Reservations &amp; Inquiries)</p>
                          <a href="tel:0744399508" className="text-red-600 text-lg font-semibold hover:text-red-700 cursor-pointer block">
                            07 4439 9508
                          </a>
                        </div>
                        <div className="border-t border-stone-200 pt-4">
                          <p className="text-sm text-stone-600 mb-1">Direct Line (Pick Up Orders)</p>
                          <a href="tel:0738440789" className="text-red-600 text-lg font-semibold hover:text-red-700 cursor-pointer block">
                            07 3844 0789
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-stone-50 p-8 rounded-xl shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                      <i className="ri-time-line text-2xl text-red-600"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-light text-stone-900 mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Opening Hours</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-stone-700 text-lg">Everyday</span>
                        </div>
                        <div className="text-stone-600 text-base space-y-1">
                          <div>11:30 AM – 2:30 PM</div>
                          <div>5:00 PM – 9:30 PM</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-stone-50 p-4 rounded-xl shadow-lg">
                <div className="w-full h-full min-h-[500px] bg-stone-100 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3540.0!2d153.0131506!3d-27.4769843!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b9150a74972438d%3A0xb125a0e5e1135fa4!2sOkuman%20Karaage%20%26%20Ramen%20Bar!5e0!3m2!1sen!2sau!4v1234567890123!5m2!1sen!2sau"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Okuman Karaage & Ramen Bar location map"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-stone-900 text-white py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <img
                  src="https://static.readdy.ai/image/c4ebca1b92e2c1129f4e15d5fe484a6a/2c6f5f26358009c83babaf47230293ba.png"
                  alt="Okuman Karaage & Ramen Bar"
                  className="h-14 w-auto mb-4"
                />
                <p className="text-stone-400">Authentic karaage and ramen in South Brisbane</p>
              </div>
              <div>
                <h4 className="text-lg font-light mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Quick Links</h4>
                <div className="space-y-2">
                  <button onClick={() => scrollToSection('home')} className="block text-stone-400 hover:text-white transition-colors cursor-pointer">Home</button>
                  <button onClick={() => scrollToSection('about')} className="block text-stone-400 hover:text-white transition-colors cursor-pointer">About</button>
                  <button onClick={() => scrollToSection('order')} className="block text-stone-400 hover:text-white transition-colors cursor-pointer">Order</button>
                  <button onClick={() => scrollToSection('gallery')} className="block text-stone-400 hover:text-white transition-colors cursor-pointer">Gallery</button>
                  <button onClick={() => scrollToSection('visit')} className="block text-stone-400 hover:text-white transition-colors cursor-pointer">Visit Us</button>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-light mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Contact</h4>
                <div className="space-y-2 text-stone-400">
                  <p>6/220 Melbourne St</p>
                  <p>South Brisbane QLD 4101</p>
                  <a href="tel:0738440789" className="block hover:text-white transition-colors cursor-pointer">07 3844 0789</a>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-light mb-4" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Follow Us</h4>
                <div className="space-y-3">
                  <a
                    href="https://www.instagram.com/okuman_brisbane/?hl=en"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-stone-400 hover:text-white transition-colors cursor-pointer group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-stone-800 rounded-full group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:via-pink-600 group-hover:to-orange-500 transition-all">
                      <i className="ri-instagram-line text-xl"></i>
                    </div>
                    <span>Instagram</span>
                  </a>
                  <a
                    href="https://www.google.com/maps/place/Okuman+Karaage+%26+Ramen+Bar/@-27.4769843,153.0105757,17z/data=!3m1!5s0x6b915a07bf552507:0x47701d2ec9f01990!4m8!3m7!1s0x6b9150a74972438d:0xb125a0e5e1135fa4!8m2!3d-27.4769843!4d153.0131506!9m1!1b1!16s%2Fg%2F11b86hyywz?entry=ttu&g_ep=EgoyMDI1MTIwMi4wIKXMDSoASAFQAw%3D%3D#:~:text=Menu-,Reviews,-About"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-stone-400 hover:text-white transition-colors cursor-pointer group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-stone-800 rounded-full group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:via-pink-600 group-hover:to-orange-500 transition-all">
                      <i className="ri-star-line text-xl"></i>
                    </div>
                    <span>Google Review</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Delivery Modal */}
        {showDeliveryModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDeliveryModal(false)}>
            <div className="bg-white rounded-2xl max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setShowDeliveryModal(false)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-stone-400 hover:text-stone-900 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
              <h3 className="text-3xl font-light text-stone-900 mb-6 text-center" style={{ fontFamily: "'Shadows Into Light', cursive" }}>Choose Delivery Service</h3>
              <div className="space-y-4">
                <a
                  href="https://www.doordash.com/store/1008757?utm_source=mx_share&aw=cIPd3Kjo2NXpCEGq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                    <i className="ri-e-bike-2-line text-2xl text-red-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-stone-900">DoorDash</h4>
                    <p className="text-sm text-stone-600">Order via DoorDash</p>
                  </div>
                  <i className="ri-arrow-right-line text-xl text-stone-400"></i>
                </a>
                <a
                  href="https://aus.hungrypanda.co/shop?brand=hungrypanda&shopId=21443"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                    <i className="ri-e-bike-2-line text-2xl text-red-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-stone-900">Hungry Panda</h4>
                    <p className="text-sm text-stone-600">Order via Hungry Panda</p>
                  </div>
                  <i className="ri-arrow-right-line text-xl text-stone-400"></i>
                </a>
                <a
                  href="https://www.ubereats.com/au/store/okuman-karaage-%26-ramen-bar/-IXZPCE5SSaBnWLKfEXoBQ?diningMode=DELIVERY&ps=1&surfaceName="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                    <i className="ri-e-bike-2-line text-2xl text-red-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-stone-900">Uber Eats</h4>
                    <p className="text-sm text-stone-600">Order via Uber Eats</p>
                  </div>
                  <i className="ri-arrow-right-line text-xl text-stone-400"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}